import { DEFAULT_SURFACE, isSurface } from "../ground/surfaces.ts";
import { SIXTEENTHS } from "../movement/step.ts";
import { PLAYER_SOUND_REACH_TILES } from "./playback.ts";

/**
 * Footsteps (ADR-085's amendment for P1.9-T84): the hook a step plays on a surface, the surface a compiled cell
 * names, and when a walking character's next step falls. A step falls each stride of distance walked, so a character
 * that covers ground faster steps faster, and the same rule serves the page's own predicted character and every
 * other player's interpolated one. A character drawn running takes the run's stride (ADR-116 decision 27).
 */

/**
 * The tiles a character walks between two steps: half of the 3.75 tiles the walk strip's one second cycle was set to
 * cover, 48 speed units a tick, where a step falls with each of the strip's two footfalls. At the rules' `walk_speed`
 * of 43 units (3.36 tiles a second) a step falls every 558 ms. The walk's knob of the cadence.
 */
export const STEP_STRIDE_TILES = 1.875;

/**
 * The tiles a character drawn running covers between two steps (ADR-116 decision 27): what the rules' `run_speed`
 * of 60 units (4.69 tiles a second) covers between two footfalls of the look sheet's run strip, 250 ms apart, so a
 * runner's step falls with its strip. The run's knob of the cadence, and a first try: the author hears it in game
 * before P2.20-T7 is Done, as decision 27 asks, and it is the one constant to change if it sounds wrong.
 */
export const RUN_STRIDE_TILES = 1.17;

/**
 * The least time between two steps of one character, in milliseconds, whatever distance it covers.
 */
export const STEP_MIN_INTERVAL_MS = 200;

/**
 * A move of more than this many tiles between two frames is a jump (a respawn, a snap, a world swap), not a walk: it
 * steps nothing and the cadence starts afresh. The interpolator's snap distance.
 */
export const STEP_SNAP_TILES = 2;

/**
 * The least time between two steps of other players started on one page, in milliseconds: a crowd walking past
 * starts at most 12 steps a second, whoever takes them.
 */
export const OTHERS_STEP_GAP_MS = 80;

/**
 * The level a footstep plays at under its channel's volume, 0 to 1: about 13 dB under the channel's other sounds, since
 * a step falls twice a second for as long as a character walks.
 */
export const FOOTSTEP_GAIN = 0.22;

/**
 * The folder every surface's steps lie in.
 */
export const FOOTSTEP_FOLDER = "sfx/player/walk";

/**
 * A surface's step hook.
 *
 * @param surface The surface, a folder under `sfx/player/walk/`.
 * @returns The hook, `sfx/player/walk/<surface>/step`.
 */
export function footstepHook(surface: string): string {
  return `${FOOTSTEP_FOLDER}/${surface}/step`;
}

/**
 * The surface a compiled cell names: its 1-based index into the world's surface names.
 *
 * @param names The world's surface names, empty for a world compiled without them.
 * @param index The cell's value, 0 for a cell with no surface.
 * @returns The surface, or `DEFAULT_SURFACE` for a cell with none, an index past the names and a name this build does
 *   not list.
 */
export function footstepSurface(names: readonly string[], index: number): string {
  const name = index > 0 ? names[index - 1] : undefined;
  return name !== undefined && isSurface(name) ? name : DEFAULT_SURFACE;
}

/**
 * The strips through which a moving character takes no step: a dash carries it, its death lays it down and a hop
 * holds its feet off the ground (ADR-116 decision 21).
 */
const STEPLESS_STRIPS: ReadonlySet<string> = new Set(["dash", "death", "jump"]);

/**
 * Whether a moving character steps while a strip plays: while its motion draws it and through a swing or a hurt,
 * never through a dash, its death or a hop.
 *
 * @param strip The strip its playback shows, or null when its motion decides.
 * @returns True when its steps sound.
 */
export function stepsWhile(strip: string | null): boolean {
  return strip === null || !STEPLESS_STRIPS.has(strip);
}

/**
 * Whether another player's step can be heard at all: it lies inside the reach of another player's sounds
 * (`PLAYER_SOUND_REACH_TILES`). A step beyond it is never handed to the mixer.
 *
 * @param dx Its offset across from the listener, in tiles.
 * @param dy Its offset down, in tiles.
 * @returns True inside the reach.
 */
export function withinStepReach(dx: number, dy: number): boolean {
  return dx * dx + dy * dy < PLAYER_SOUND_REACH_TILES * PLAYER_SOUND_REACH_TILES;
}

/**
 * Whether another player's step may start now: the crowd's gap has passed since the last one started.
 *
 * @param now The time in milliseconds.
 * @param last When the last step of another player started, negative infinity for none.
 * @returns True when the step may start.
 */
export function crowdAdmits(now: number, last: number): boolean {
  return now - last >= OTHERS_STEP_GAP_MS;
}

const STRIDE = STEP_STRIDE_TILES * SIXTEENTHS;
/**
 * What a sixteenth covered at a run counts for in the walk's stride, which the cadence carries its distance in.
 */
const RUN_SHARE = STEP_STRIDE_TILES / RUN_STRIDE_TILES;
const SNAP = STEP_SNAP_TILES * SIXTEENTHS;

/**
 * One character's cadence: the distance it has walked since its last step, a run's counted by the share of the run's
 * stride it covers, so a change of pace between two steps keeps what was covered. It starts, and starts again after a stop or a jump, half a stride from a step, so the
 * first footfall of a walk comes a quarter of a second in. It allocates nothing as it advances.
 */
export class StepCadence {
  private seen = false;
  private x = 0;
  private y = 0;
  private carried = STRIDE / 2;
  private stepped = Number.NEGATIVE_INFINITY;

  /**
   * Moves the character to where it is drawn this frame and says whether a step falls.
   *
   * @param x Its position across, in sixteenths of a tile.
   * @param y Its position down, in sixteenths.
   * @param walking Whether it walks: moving, alive and playing neither a dash nor its death (`stepsWhile`); it
   *   walks through a swing and a hurt.
   * @param now The time in milliseconds.
   * @param running Whether it is drawn running, which measures this frame's move by the run's stride; false when
   *   not given. It is the running bit of its view or the page's own prediction, never its speed.
   * @returns True when a step falls this frame.
   */
  public advance(x: number, y: number, walking: boolean, now: number, running = false): boolean {
    const moved = this.seen ? Math.hypot(x - this.x, y - this.y) : 0;
    this.seen = true;
    this.x = x;
    this.y = y;
    if (!walking || moved > SNAP) {
      this.carried = STRIDE / 2;
      return false;
    }
    this.carried += running ? moved * RUN_SHARE : moved;
    if (this.carried < STRIDE) {
      return false;
    }
    if (now - this.stepped < STEP_MIN_INTERVAL_MS) {
      this.carried = STRIDE;
      return false;
    }
    this.carried = Math.min(this.carried - STRIDE, STRIDE);
    this.stepped = now;
    return true;
  }

  /**
   * Forgets where the character stood and what it carried, as a session starts or its handle leaves the view.
   */
  public reset(): void {
    this.seen = false;
    this.carried = STRIDE / 2;
    this.stepped = Number.NEGATIVE_INFINITY;
  }
}
